

/* ==========================================================================
   SHARE STATE + PORTABLE /db <-> /3d BUILD BRIDGE (/database/share-state.js)

   v1: legacy requirement/name payloads (still decodable)
   v2: backend universal-ID build payload + requirement fallback

   Catalog identity is NEVER generated in the browser. Exact component IDs are
   consumed from the backend catalog and only retained while their specifications
   remain unmodified.
   ========================================================================== */

import { state, getSelectedComponent, getCaseModeSelection } from './state.js?v=20260819-audit2';
import { slugifyUrlSegment, caseRouteSlug } from './utils.js?v=20260819-audit2';
import { debugRuntimeIssue } from './runtime.js?v=20260819-audit2';
import { catalogIdOf } from './catalog-identity.js?v=20260819-audit2';

const SharedContract = () => window.SFFContracts;
export const SFF_BUILD_SCHEMA = SharedContract()?.BUILD_SCHEMA || 2;
export const SFF_IDENTITY_SCHEMA = SharedContract()?.IDENTITY_SCHEMA || 1;

function validateLegacyRequirements(parsed = {}) {
  return SharedContract()?.validateRequirements?.(parsed) || null;
}

function validId(value) {
  return SharedContract()?.validCatalogId?.(value) || '';
}

function decodePortablePayload(parsed) {
  return SharedContract()?.normalizeBuildPayload?.(parsed) || null;
}

function legacyCompact(specs = {}) {
  return {
    m: specs.mobo,
    p: specs.psu,
    v: specs.vol,
    gl: specs.gpuL,
    gt: specs.gpuT,
    gh: specs.gpuH,
    pc: specs.pcie,
    c: specs.cool,
    ch: specs.cpuH,
    r: specs.rad,
    f: specs.fan,
    rh: specs.ramH,
    rn: specs.ramModules,
    s: specs.search || undefined,
    gs: specs.gpuSearch || undefined,
    cs: specs.cpuSearch || undefined,
    as: specs.aioSearch || undefined,
    ms: specs.moboSearch || undefined,
    rs: specs.ramSearch || undefined,
    rep: specs.report_id || undefined
  };
}

function selectedRef(kind) {
  const selected = getSelectedComponent(kind);
  if (!selected) return null;

  const id = !selected.customized ? validId(selected.catalogId || selected.catalog_id || selected.id) : null;
  if (id) {
    const ref = { id };
    if (kind === 'ram') ref.modules = Math.max(1, Math.min(8, Number(selected.modules) || 2));
    return ref;
  }

  const original = selected.original_specs || selected;
  let dims = null;
  if (kind === 'gpu') {
    dims = { l: Number(original.length_mm) || 0, w: Number(original.thickness_mm) || 0, h: Number(original.height_mm) || 0 };
  } else if (kind === 'cpu') {
    dims = { l: Number(original.length_mm) || 0, w: Number(original.width_mm) || 0, h: Number(original.height_mm) || 0 };
  } else if (kind === 'aio') {
    dims = { l: Number(original.actual_length_mm) || 0, w: Number(original.actual_width_mm) || 0, h: Number(original.actual_thickness_mm) || 0 };
  } else if (kind === 'ram') {
    dims = { l: 133, w: 7, h: Number(original.height_mm || original.ram_height_mm || original.height) || 0 };
  } else if (kind === 'psu') {
    dims = {
      l: Number(original.length_mm || original.length) || 0,
      w: Number(original.width_mm || original.width) || 0,
      h: Number(original.height_mm || original.height) || 0
    };
  }

  if (!dims || !Object.values(dims).some(v => v > 0)) return null;
  const ref = { custom: true, label: String(selected.name || selected.model_query || 'Custom'), dims };
  if (kind === 'ram') ref.modules = Math.max(1, Math.min(8, Number(selected.modules) || 2));
  return ref;
}

function findActiveCase(activeCaseName) {
  const target = String(activeCaseName || '').trim();
  if (target) {
    const slug = caseRouteSlug(target);
    const explicit = state.fetchedCasesGlobal?.find(c => caseRouteSlug(c) === slug) || null;
    if (explicit) return explicit;
  }
  if (state.selectedCaseIdx !== null && state.fetchedCasesGlobal?.[state.selectedCaseIdx]) return state.fetchedCasesGlobal[state.selectedCaseIdx];
  return null;
}

function activeSpineStepId(activeCase) {
  const idx = activeCase ? state.fetchedCasesGlobal?.indexOf(activeCase) : -1;
  const result = idx >= 0 ? state.resultByIdx?.get?.(idx) : null;
  const step = result?.spineStep || null;
  const resolved = String(step?.slotName || step?.id || step?.mode_id || step?.name || '').trim();
  return resolved || String(state.portableFixedStep || '').trim() || null;
}

export function buildPortableBuildPayload(activeCaseName, specs = {}) {
  const activeCase = findActiveCase(activeCaseName);
  const caseId = validId(catalogIdOf(activeCase));
  const caseRef = caseId ? { id: caseId } : null;
  const requirement = validateLegacyRequirements(legacyCompact(specs)) || {};

  const boardDims = {
    ITX: { l: 170, w: 170, h: 1.6 },
    DTX: { l: 203, w: 170, h: 1.6 },
    MATX: { l: 244, w: 244, h: 1.6 },
    ATX: { l: 305, w: 244, h: 1.6 }
  };
  const boardKey = String(specs.mobo || 'ITX').toUpperCase()
    .replace(/M[-_ ]?ATX|MICRO[-_ ]?ATX/, 'MATX')
    .replace(/MINI[-_ ]?ITX/, 'ITX');
  const mobo = selectedRef('mobo') || { custom: true, label: `${boardKey} Motherboard`, dims: boardDims[boardKey] || boardDims.ITX };

  const gpu = selectedRef('gpu') || ((Number(specs.gpuL) > 0 && Number(specs.gpuT) > 0 && Number(specs.gpuH) > 0)
    ? { custom: true, label: 'Custom GPU Requirement', dims: { l: Number(specs.gpuL), w: Number(specs.gpuT), h: Number(specs.gpuH) } }
    : null);

  const cooling = String(specs.cool || 'air').toLowerCase();
  const air = selectedRef('cpu') || (cooling === 'air' && Number(specs.cpuH) > 0
    ? { custom: true, label: 'Custom Air Cooler Requirement', dims: { l: 95, w: 95, h: Number(specs.cpuH) } }
    : null);

  const radSize = Number(specs.rad) || 0;
  const aioLen = radSize >= 360 ? 395 : (radSize >= 280 ? 315 : (radSize >= 240 ? 275 : (radSize > 0 ? 155 : 0)));
  const aio = selectedRef('aio') || (cooling === 'liquid' && aioLen > 0
    ? { custom: true, label: `Custom ${radSize}mm AIO`, dims: { l: aioLen, w: radSize === 280 ? 140 : 120, h: 27.2 } }
    : null);

  const psuKey = String(specs.psu || 'SFX').toUpperCase().replace(/SFXL/, 'SFX-L').replace(/FLEX[-_ ]?ATX/, 'FLEX');
  const psuDims = {
    SFX: { l: 125, w: 63.5, h: 100 },
    'SFX-L': { l: 125, w: 63.5, h: 125 },
    ATX: { l: 150, w: 86, h: 140 },
    FLEX: { l: 150, w: 40.5, h: 81.5 }
  };
  const psu = selectedRef('psu') || state.portableBuildHardware?.psu || (psuDims[psuKey]
    ? { custom: true, label: `${psuKey} Power Supply`, dims: psuDims[psuKey] }
    : null);

  const ramHeight = Number(specs.ramH) || 35;
  const ramModules = Math.max(1, Math.min(8, Number(specs.ramModules) || 2));
  const ram = selectedRef('ram') || state.portableBuildHardware?.ram || {
    custom: true,
    label: 'Custom RAM Requirement',
    dims: { l: 133, w: 7, h: ramHeight },
    modules: ramModules
  };
  if (ram) ram.modules = Math.max(1, Math.min(8, Number(ram.modules) || ramModules));

  return {
    schema: SFF_BUILD_SCHEMA,
    identitySchema: SFF_IDENTITY_SCHEMA,
    case: caseRef,
    modeId: activeCase ? getCaseModeSelection(activeCase, 'default') : null,
    // Carry the spine step that the deterministic /db evaluation is actually using.
    // Imported documented state remains the fallback when the result is not yet cached.
    fixedStep: activeSpineStepId(activeCase),
    hardware: { motherboard: mobo, gpu, psu, aircooler: air, aio, ram },
    requirements: requirement
  };
}

export function encodePortableBuildPayload(payload) {
  try {
    return SharedContract()?.encodeBuildToken?.(payload) || '';
  } catch (error) {
    debugRuntimeIssue('Portable Build Encode', error);
    return '';
  }
}

export function encodeStateToShortcode(specs, activeCaseName = null) {
  try {
    return encodePortableBuildPayload(buildPortableBuildPayload(activeCaseName, specs || {}));
  } catch (error) {
    debugRuntimeIssue('Share State', error);
    return '';
  }
}

export function decodeShortcodeToState(shortcode) {
  if (!shortcode || typeof shortcode !== 'string' || shortcode.length > 8192) return null;
  try {
    const portable = SharedContract()?.decodeBuildToken?.(shortcode) || null;
    if (portable) return { ...portable.requirements, _portableBuild: portable };
    const legacy = SharedContract()?.decodeJsonToken?.(shortcode);
    return legacy ? validateLegacyRequirements(legacy) : null;
  } catch (error) {
    debugRuntimeIssue('Share State Decode', error);
    return null;
  }
}

export function buildHybridShareUrl(activeCaseName, specs) {
  const payload = buildPortableBuildPayload(activeCaseName, specs || {});
  const fragment = String(location.hash || '');
  const contract = SharedContract();
  if (!contract) return '';

  if (state.sffPinnedCases?.length === 2) {
    const caseA = state.fetchedCasesGlobal?.[state.sffPinnedCases[0]];
    const caseB = state.fetchedCasesGlobal?.[state.sffPinnedCases[1]];
    if (caseA && caseB) return `${window.location.origin}${contract.comparePath(caseA, caseB, payload)}${fragment}`;
  }

  const segments = location.pathname.split('/').filter(Boolean);
  const compareIndex = segments.indexOf('compare');
  if (compareIndex !== -1 && segments.length > compareIndex + 1) {
    const token = contract.encodeBuildToken(payload);
    return `${window.location.origin}/db/compare/${segments[compareIndex + 1]}/${token ? `${token}/` : ''}${fragment}`;
  }

  const activeCase = findActiveCase(activeCaseName);
  const target = activeCase || activeCaseName || state.lastHydratedCaseSlug || 'build';
  return `${window.location.origin}${contract.dbCasePath(target, payload)}${fragment}`;
}

export function build3DBridgeUrl(activeCaseName, specs = {}) {
  const activeCase = findActiveCase(activeCaseName);
  const payload = buildPortableBuildPayload(activeCase?.name || activeCaseName, specs);
  return SharedContract()?.workbenchPath?.(activeCase || activeCaseName || 'build', payload) || '/3d/';
}

if (typeof window !== 'undefined') {
  window.getSffPortableBuildPayload = (activeCaseName = null, specs = {}) => buildPortableBuildPayload(activeCaseName, specs);
  window.decodeSffBuildToken = decodeShortcodeToState;
}
